/* eslint-disable indent */
import { UploadOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Segmented, Typography, Upload } from "antd";
import axios from "axios";
import { useState } from "react";
import * as XLSX from "xlsx";

import { showErrorNotification, showSuccessNotification } from "../../../../utils/notification";
import { WAITLIST_API_ENDPOINTS } from "../../constants";

const { Item } = Form;

const { Text } = Typography;

const ADD_ENTRIES_MODES = Object.freeze({
  SINGLE: "SINGLE",
  BULK: "BULK"
});

const MODE_HINTS = Object.freeze({
  [ADD_ENTRIES_MODES.SINGLE]: "Add one email address to the waitlist.",
  [ADD_ENTRIES_MODES.BULK]:
    "Paste emails (comma/newline separated) or upload a CSV/XLSX with emails in the first column. Duplicates and invalid addresses are skipped by the server."
});

/**
 * Splits a bulk textarea's raw text into individual emails on commas/newlines, trimming empties.
 * The backend does the real validation/dedup and reports counts back.
 *
 * @example
 * parseBulkEmails("one@example.com, two@example.com\nthree@example.com ");
 * // ["one@example.com", "two@example.com", "three@example.com"]
 */
const parseBulkEmails = (rawText) =>
  (rawText || "")
    .split(/[\n,]/)
    .map((email) => email.trim())
    .filter((email) => email);

// Reads emails from the first column of a CSV/XLSX file, entirely client-side.
const extractEmailsFromFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const workbook = XLSX.read(event.target.result, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        resolve(rows.map((row) => `${row?.[0] ?? ""}`.trim()).filter(Boolean));
      } catch (parseError) {
        reject(parseError);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

const AddEntriesModal = ({
  configId,
  ROOT_URL,
  isModalOpen = false,
  setIsModalOpen = () => {},
  onSuccess = () => {}
}) => {
  const [form] = Form.useForm();
  const [mode, setMode] = useState(ADD_ENTRIES_MODES.SINGLE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bulkRaw, setBulkRaw] = useState("");

  const parsedEmailCount = mode === ADD_ENTRIES_MODES.BULK ? parseBulkEmails(bulkRaw).length : 0;

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    form.resetFields();
    setBulkRaw("");
  };

  // Parses an uploaded CSV/XLSX and appends its emails into the bulk textarea. Returns false to
  // stop antd from also uploading the file anywhere — the parse is entirely client-side.
  const handleFileSelected = async (file) => {
    try {
      const emails = await extractEmailsFromFile(file);
      const mergedEmails = [...parseBulkEmails(bulkRaw), ...emails].join("\n");
      setBulkRaw(mergedEmails);
      form.setFieldsValue({ bulkEmails: mergedEmails });
      showSuccessNotification(
        `Loaded ${emails.length} ${emails.length === 1 ? "email" : "emails"} from ${file.name}.`
      );
    } catch {
      showErrorNotification(
        "Could not read that file. Use a CSV/XLSX with emails in the first column."
      );
    }
    return false;
  };

  const handleFormSubmit = (values) => {
    setIsSubmitting(true);

    const request =
      mode === ADD_ENTRIES_MODES.SINGLE
        ? axios.post(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${configId}/entries`, {
            email: values.email
          })
        : axios.post(`${ROOT_URL}/${WAITLIST_API_ENDPOINTS.CONFIGS}/${configId}/entries/import`, {
            emails: parseBulkEmails(values.bulkEmails)
          });

    request
      .then((res) => {
        if (mode === ADD_ENTRIES_MODES.SINGLE) {
          showSuccessNotification("Entry added successfully!");
        } else {
          const { addedCount = 0, duplicateOrInvalidCount = 0 } = res.data || {};
          showSuccessNotification(
            `Added ${addedCount}, skipped ${duplicateOrInvalidCount} duplicates/invalid.`
          );
        }
        setIsModalOpen(false);
        onSuccess();
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Modal
      title="Add Entries"
      open={isModalOpen}
      footer={null}
      onCancel={handleCancel}
      destroyOnClose
      width={520}
    >
      <Segmented
        block
        options={[
          { label: "Single Email", value: ADD_ENTRIES_MODES.SINGLE },
          { label: "Bulk Import", value: ADD_ENTRIES_MODES.BULK }
        ]}
        value={mode}
        onChange={handleModeChange}
        style={{ marginBottom: 8 }}
      />

      {/* Mode hint */}
      <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 16 }}>
        {MODE_HINTS[mode]}
      </Text>

      <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
        {mode === ADD_ENTRIES_MODES.SINGLE ? (
          <Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter an email!" },
              { type: "email", message: "Please enter a valid email!" }
            ]}
          >
            <Input placeholder="user@example.com" />
          </Item>
        ) : (
          <>
            <Upload
              accept=".csv,.xlsx,.xls"
              maxCount={1}
              showUploadList={false}
              beforeUpload={handleFileSelected}
            >
              <Button icon={<UploadOutlined />} style={{ marginBottom: 12 }}>
                Upload CSV / XLSX
              </Button>
            </Upload>
            <Item
              name="bulkEmails"
              label={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center"
                  }}
                >
                  <span>Emails</span>
                  {parsedEmailCount > 0 && (
                    <Text type="secondary" style={{ fontSize: 12, fontWeight: "normal" }}>
                      {parsedEmailCount} {parsedEmailCount === 1 ? "email" : "emails"} detected
                    </Text>
                  )}
                </div>
              }
              rules={[{ required: true, message: "Please enter at least one email!" }]}
            >
              <Input.TextArea
                rows={8}
                placeholder={"user1@example.com\nuser2@example.com"}
                onChange={(e) => setBulkRaw(e.target.value)}
              />
            </Item>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isSubmitting}>
            {mode === ADD_ENTRIES_MODES.BULK && parsedEmailCount > 0
              ? `Add ${parsedEmailCount} ${parsedEmailCount === 1 ? "entry" : "entries"}`
              : "Add"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default AddEntriesModal;
